import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Avatar } from '@/components/Avatar';
import { Button } from '@/ui/Button';

/**
 * BuscadorDePersonas — buscar a alguien del directorio y elegirlo.
 *
 * ## De dónde sale
 *
 * Existía dos veces, palabra por palabra: en `celebraciones/piezas.tsx` y en
 * `reconocimientos/Compositor.tsx`, cada una con su propio `useDirectorio`
 * apuntando al mismo `/users/directory`. Al llegar el módulo 9 iba a hacer
 * falta una tercera —para añadir miembros a una comunidad— y esa es exactamente
 * la casilla que el método prohíbe: tres copias del mismo objeto divergiendo a
 * la primera corrección.
 *
 * Se recuenta en la ficha del módulo 9: **2 copias → 1**, y con ellas dos
 * `useDirectorio` que consultaban lo mismo con dos claves de caché distintas
 * (`['usuarios','directorio',q]` y `['celebraciones','directorio',q]`), así que
 * la misma búsqueda se pedía dos veces si alguien pasaba por las dos pantallas.
 *
 * `useDirectorio` de `features/directorio/api.ts` **no** entra aquí y no es un
 * descuido: ese pagina la pantalla entera del Directorio con sus filtros. Mismo
 * endpoint, otro trabajo.
 *
 * ## Lo que este componente arregla del vanilla que sustituye
 *
 * En `page-communities.js` los resultados eran `<div onclick>` con
 * `onmouseover`/`onmouseout` escribiendo `style.background` a mano: sin foco,
 * sin Enter, invisibles para un lector de pantalla. Aquí son `<button>` dentro
 * de un `role="listbox"`, con el estado de paso del ratón en CSS.
 */

export interface PersonaDelDirectorio {
  id: number;
  full_name: string;
  position?: string | null;
}

/**
 * Dos caracteres es el umbral que ya tenía el vanilla y sigue siendo el
 * correcto: con una letra la lista sale con ocho desconocidos.
 */
const MINIMO_PARA_BUSCAR = 2;

function useDirectorio(busqueda: string, limite: number) {
  const limpia = busqueda.trim();
  return useQuery({
    queryKey: ['directorio', 'buscador', limpia, limite] as const,
    queryFn: () =>
      api.get<{ items?: PersonaDelDirectorio[] }>('/users/directory', {
        search: limpia,
        limit: limite,
      }),
    enabled: limpia.length >= MINIMO_PARA_BUSCAR,
    staleTime: 60_000,
  });
}

export function BuscadorDePersonas({
  id, etiqueta, marcador, limite = 8, elegida, alElegir, alSoltar,
  excluir, tamanoDelAvatar = 32,
}: {
  id: string;
  /** Qué se está buscando. Va en `sr-only`: el `<label>` del campo ya se ve. */
  etiqueta: string;
  marcador?: string;
  limite?: number;
  /** Si se pasa y no es nula, el buscador se sustituye por la ficha de quien se
   *  eligió. Una pantalla que añade varias personas seguidas no la usa. */
  elegida?: PersonaDelDirectorio | null;
  alElegir: (p: PersonaDelDirectorio) => void;
  /** Si se pasa, se ofrece deshacer la elección. */
  alSoltar?: () => void;
  /** Ids que no deben salir en los resultados. Lo usa «Miembros»: ofrecer a
   *  alguien que ya está dentro terminaba en un 400 del servidor —«El usuario
   *  ya es miembro»— después de pulsar. */
  excluir?: number[];
  tamanoDelAvatar?: number;
}) {
  const [busqueda, setBusqueda] = useState('');
  const campo = useRef<HTMLInputElement>(null);
  const { data, isFetching, isError } = useDirectorio(busqueda, limite);
  const fuera = new Set(excluir ?? []);
  const personas = (data?.items ?? []).filter((p) => !fuera.has(p.id));
  const corta = busqueda.trim().length < MINIMO_PARA_BUSCAR;

  if (elegida) {
    return (
      <div className="flex items-center gap-2 rounded-sm bg-brand/[.06] px-3 py-2 text-3 text-brand-ink">
        <i className="bi bi-person-check-fill" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">
          {elegida.full_name}
          {elegida.position ? ` · ${elegida.position}` : ''}
        </span>
        {alSoltar && (
          <Button variant="ghost" size="sm" onClick={() => { alSoltar(); setBusqueda(''); }}>
            Cambiar
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <label>
        <span className="sr-only">{etiqueta}</span>
        <input
          ref={campo}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={!corta}
          aria-controls={`${id}-lista`}
          autoComplete="off"
          placeholder={marcador ?? 'Escribe al menos 2 letras del nombre…'}
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="w-full rounded-sm border border-field-border bg-surface px-3 py-2
                     text-3 text-ink focus-visible:focus-anillo pointer-coarse:min-h-11"
        />
      </label>
      {!corta && (
        <ul
          id={`${id}-lista`}
          role="listbox"
          className="absolute left-0 top-full z-10 max-h-44 w-full overflow-y-auto
                     rounded-sm border border-line bg-raised shadow-md"
        >
          {isFetching && !personas.length && (
            <li className="px-3 py-2.5 text-3 text-muted">Buscando…</li>
          )}
          {/* El vanilla se tragaba el fallo con un `catch {}` y dejaba en
              pantalla la lista ANTERIOR, que desde fuera es idéntico a un
              resultado. */}
          {isError && (
            <li className="px-3 py-2.5 text-3 text-danger-ink">
              No se pudo buscar. Inténtalo otra vez.
            </li>
          )}
          {!isFetching && !isError && !personas.length && (
            /* El término va como TEXTO. En el vanilla se concatenaba en una
               cadena de HTML sin escapar, así que buscar `<b>x` pintaba negrita
               dentro del propio mensaje de «sin resultados». */
            <li className="px-3 py-2.5 text-3 text-muted">
              Sin resultados para «{busqueda.trim()}»
            </li>
          )}
          {personas.map((p) => (
            <li key={p.id}>
              {/* Un <button>, no un <div onclick>: recibe foco, responde a
                  Enter y el lector de pantalla lo anuncia como algo pulsable. */}
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="flex w-full items-center gap-2.5 border-b border-line px-3.5 py-2.5
                           text-left text-3 last:border-0 hover:bg-sunken focus-visible:focus-anillo"
                onClick={() => {
                  alElegir(p);
                  setBusqueda('');
                  // Sin `elegida`, la pantalla añade varias personas seguidas. El
                  // botón pulsado desaparece con la lista y el foco caía al
                  // `<body>`: con teclado, cada persona obligaba a volver a buscar
                  // el campo. El vanilla de las asignaciones ya lo devolvía.
                  if (elegida === undefined) campo.current?.focus();
                }}
              >
                <Avatar nombre={p.full_name} tamano={tamanoDelAvatar} />
                <span className="min-w-0">
                  <span className="block truncate font-semibold">{p.full_name}</span>
                  {p.position && (
                    <span className="block truncate text-2 text-muted">{p.position}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
